import type { ShotDetailsPayload, ShotDetailsSceneContext } from "@/components/shot-details-dialog"
import type { ShotTimelineEditorData } from "@/components/shot-panel/timeline-editor/types"
import type { StoryboardShotN } from "@/components/storyboard-shot-tile"
import type { LocationStorableFacing, LocationViewpointRow } from "@/lib/locations-api"
import type {
  GenerationJobRow,
  GenerationModelsByContext,
  ProjectPermissions,
  ProjectSummary,
  WorkspaceBaseProps,
} from "@/types/workspace"
import i18n from "@/i18n"
import { getCsrfToken } from "@/lib/csrf"

export { getCsrfToken }

export async function uploadProjectCover(projectId: number, file: File) {
  const form = new FormData()
  form.append("file", file)
  const res = await fetch(`/projects/${projectId}/workspace/cover`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "same-origin",
    body: form,
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; cover_image_url?: string | null; error?: string }
  if (!res.ok || data.ok === false) {
    throw new Error(data.error ?? `HTTP ${res.status}`)
  }
  return data
}

export type SeedanceVideoConsentOwner = { type: string; id: number; label: string }

export type CheckSeedanceVideoConsentResponse = {
  ok: boolean
  needs_consent: boolean
  shot_ids: number[]
  owners: SeedanceVideoConsentOwner[]
  agreement_key: string
}

export async function checkSeedanceVideoConsent(
  projectId: number,
  shotIds: number[],
): Promise<CheckSeedanceVideoConsentResponse> {
  const verb = "check_seedance_video_consent"
  const res = await fetch(`/projects/${projectId}/workspace/run`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      verb,
      shot_ids: shotIds,
      workspace: { verb, shot_ids: shotIds },
    }),
  })
  const data = (await res.json().catch(() => ({}))) as Partial<CheckSeedanceVideoConsentResponse> & {
    error?: string
  }
  if (!res.ok || data.ok === false) {
    throw new Error(data.error ?? i18n.t("workspace:seedance.videoConsentCheckFailed"))
  }
  return data as CheckSeedanceVideoConsentResponse
}

export type ShotWorkspaceSceneContextApi = {
  season_number: number
  season_title: string | null
  episode_number: number
  episode_title: string | null
  scene_id: number
  scene_number: number
  scene_title: string | null
  scene_purpose: string | null
  location_name: string | null
}

export type ShotWorkspaceDetailResponse = {
  scene_context: ShotWorkspaceSceneContextApi
  shot: ShotDetailsPayload & {
    storyboard_image_prompt_default?: string | null
    storyboard_system_prompt_default?: string | null
    shooting_board_panel_count?: number
    shooting_board_image_prompt?: string | null
    shooting_board_image_prompt_doc?: Record<string, unknown> | null
    shooting_board_image_prompt_default?: string | null
    video_kling_prompt?: string | null
    video_kling_prompt_default?: string | null
    video_system_prompt?: string | null
    video_system_prompt_default?: string | null
    storyboard_image_prompt_doc?: Record<string, unknown> | null
    video_kling_prompt_doc?: Record<string, unknown> | null
    seedance_video_seed?: number | null
    generation_unseen?: boolean
    llm_extras?: Record<string, unknown> | null
    character_ids?: number[]
    location_id?: number | null
    active_video_timeline_markers?: { id: number; mark_ms: number }[]
    timeline_editor?: ShotTimelineEditorData | null
  }
}

function mapSceneContext(raw: ShotWorkspaceSceneContextApi): ShotDetailsSceneContext {
  return {
    seasonNumber: raw.season_number,
    seasonTitle: raw.season_title,
    episodeNumber: raw.episode_number,
    episodeTitle: raw.episode_title,
    sceneId: raw.scene_id,
    sceneNumber: raw.scene_number,
    sceneTitle: raw.scene_title,
    scenePurpose: raw.scene_purpose,
    locationName: raw.location_name,
  }
}

type ShotWorkspaceDetailResult = {
  sceneContext: ShotDetailsSceneContext
  shot: ShotWorkspaceDetailResponse["shot"]
}

/** In-flight coalescing: parallel openers (panel + prompt) share one GET /detail. */
const shotDetailInflight = new Map<string, Promise<ShotWorkspaceDetailResult>>()

function shotDetailCacheKey(projectId: number, shotId: number): string {
  return `${projectId}:${shotId}`
}

export async function fetchShotWorkspaceDetail(projectId: number, shotId: number): Promise<ShotWorkspaceDetailResult> {
  const key = shotDetailCacheKey(projectId, shotId)
  const existing = shotDetailInflight.get(key)
  if (existing) return existing

  const promise = (async (): Promise<ShotWorkspaceDetailResult> => {
    const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/detail`, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
    })
    if (!res.ok) {
      throw new Error(i18n.t("shot:apiErrors.loadShotFailed", { status: res.status }))
    }
    const data = (await res.json()) as ShotWorkspaceDetailResponse
    return { sceneContext: mapSceneContext(data.scene_context), shot: data.shot }
  })().finally(() => {
    // Drop only this promise — a newer refresh may already occupy the key.
    if (shotDetailInflight.get(key) === promise) {
      shotDetailInflight.delete(key)
    }
  })

  shotDetailInflight.set(key, promise)
  return promise
}

export type ShotTimelineVoiceOption = {
  kind: "voice" | "library_default"
  voice_id: number | null
  voice_name: string | null
  character_id: number
  character_name: string
  provider_kind: string
  provider_voice_id: string | null
  active: boolean
  preview_audio_url: string | null
}

export type ShotTimelineLibraryVoiceRef = {
  provider_kind: string
  provider_voice_id: string
  name: string | null
  preview_url: string | null
}

export type ShotTimelineVoicesResponse = {
  voices: ShotTimelineVoiceOption[]
  default_library_voice: ShotTimelineLibraryVoiceRef | null
  sts_provider_kinds: string[]
}

export async function fetchShotTimelineVoices(projectId: number, shotId: number): Promise<ShotTimelineVoicesResponse> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/timeline_voices`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
  })
  if (!res.ok) {
    throw new Error(i18n.t("shot:apiErrors.loadShotFailed", { status: res.status }))
  }
  return (await res.json()) as ShotTimelineVoicesResponse
}

export type ShotPanelBootstrap = {
  project: ProjectSummary
  project_permissions: ProjectPermissions
  generation_models_by_context: GenerationModelsByContext
  sketch: NonNullable<WorkspaceBaseProps["sketch"]>
}

export async function fetchShotPanelBootstrap(projectId: number): Promise<ShotPanelBootstrap> {
  const res = await fetch(`/projects/${projectId}/workspace/shot_panel_bootstrap`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
  })
  if (!res.ok) {
    throw new Error(i18n.t("shot:apiErrors.loadShotFailed", { status: res.status }))
  }
  return (await res.json()) as ShotPanelBootstrap
}

export type StoryBodyResponse = {
  body: string
  review_state: string | null
}

export type PatchShotWorkspacePromptsBody = {
  storyboard_system_prompt?: string
  storyboard_image_prompt?: string
  storyboard_send_current_as_reference?: boolean
  storyboard_animatic_reference_frame?: string
  storyboard_image_prompt_doc?: Record<string, unknown> | null
  video_kling_prompt?: string
  video_kling_prompt_doc?: Record<string, unknown> | null
  video_system_prompt?: string
  shooting_board_image_prompt?: string
  shooting_board_image_prompt_doc?: Record<string, unknown> | null
  seedance_send_animatic_reference?: boolean
  seedance_animatic_reference_mode?: "video_silent" | "video_with_audio" | "audio_only"
  /** Превиз как видео-референс движения камеры; взаимоисключается с аниматиком. */
  seedance_send_previz_reference?: boolean
  /** Маска глубины вместо пролёта. Учитывается только вместе с отправкой превиза. */
  seedance_send_previz_depth?: boolean
  /** gray — по умолчанию, vis — цветная inferno. */
  seedance_previz_depth_variant?: "gray" | "vis"
  seedance_use_shooting_board?: boolean
  seedance_video_seed?: string
  /** true — сохранить текст/doc без создания версии в истории (автосохранение). */
  skip_prompt_version?: boolean
}

/** Сохранение промптов сториборда + видео одним запросом; JSON без Inertia reload. */
export async function patchShotWorkspacePrompts(
  projectId: number,
  shotId: number,
  body: PatchShotWorkspacePromptsBody,
): Promise<StoryboardShotN> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/workspace_prompts`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify(body),
  })
  const data = (await res.json()) as { ok?: boolean; error?: string; shot?: StoryboardShotN }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
  if (!data.shot) {
    throw new Error(i18n.t("shot:apiErrors.noShotInResponse"))
  }
  return data.shot
}

export type PatchShotPromptSettingsBody = {
  storyboard_send_current_as_reference?: boolean
  storyboard_animatic_reference_frame?: string
  seedance_send_animatic_reference?: boolean
  seedance_animatic_reference_mode?: "video_silent" | "video_with_audio" | "audio_only"
  /** Превиз как видео-референс движения камеры; взаимоисключается с аниматиком. */
  seedance_send_previz_reference?: boolean
  /** Маска глубины вместо пролёта. Учитывается только вместе с отправкой превиза. */
  seedance_send_previz_depth?: boolean
  /** gray — по умолчанию, vis — цветная inferno. */
  seedance_previz_depth_variant?: "gray" | "vis"
  seedance_use_shooting_board?: boolean
  video_send_storyboard_frame?: boolean
  video_consider_scale?: boolean
  video_consider_prop_scale?: boolean
  seedance_video_seed?: string
  shooting_board_panel_count?: number
}

/** Мгновенное сохранение настроек экрана промпта (без правки текста промптов и версий). */
export async function patchShotPromptSettings(
  projectId: number,
  shotId: number,
  body: PatchShotPromptSettingsBody,
): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/prompt_settings`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify(body),
  })
  const data = (await res.json()) as {
    ok?: boolean
    error?: string
  }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
}

/** Краткое назначение кадра (intent_summary); JSON без Inertia reload. */
export async function patchShotIntentSummary(
  projectId: number,
  shotId: number,
  intentSummary: string,
): Promise<StoryboardShotN> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/intent_summary`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ intent_summary: intentSummary }),
  })
  const data = (await res.json()) as { ok?: boolean; error?: string; shot?: StoryboardShotN }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
  if (!data.shot) {
    throw new Error(i18n.t("shot:apiErrors.noShotInResponse"))
  }
  return data.shot
}

export type WorkspaceSceneOption = {
  id: number
  number: number
  title: string | null
  season_number: number
  episode_number: number
  episode_title: string | null
}

export async function fetchSceneOptions(projectId: number): Promise<WorkspaceSceneOption[]> {
  const res = await fetch(`/projects/${projectId}/workspace/scene_options`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
  })
  const data = (await res.json()) as { ok?: boolean; error?: string; scenes?: WorkspaceSceneOption[] }
  if (!res.ok || data.ok === false) {
    throw new Error(data.error ?? i18n.t("shot:apiErrors.loadShotFailed", { status: res.status }))
  }
  return data.scenes ?? []
}

export type MovedShotSceneResult = StoryboardShotN & {
  scene_id: number
  scene_number: number
}

/** Перенос шота в другую сцену (append + новый Dewey-номер). */
export async function patchShotScene(
  projectId: number,
  shotId: number,
  sceneId: number,
): Promise<MovedShotSceneResult> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/scene`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ scene_id: sceneId }),
  })
  const data = (await res.json()) as { ok?: boolean; error?: string; shot?: MovedShotSceneResult }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
  if (!data.shot) {
    throw new Error(i18n.t("shot:apiErrors.noShotInResponse"))
  }
  return data.shot
}

export async function fetchStoryBody(projectId: number): Promise<StoryBodyResponse> {
  const res = await fetch(`/projects/${projectId}/workspace/story_body`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
  })
  if (!res.ok) {
    throw new Error(i18n.t("shot:apiErrors.loadStoryFailed", { status: res.status }))
  }
  return res.json() as Promise<StoryBodyResponse>
}

export type ProjectCastOutfit = {
  id: number
  name: string
  reference_url?: string | null
  reference_full_url?: string | null
  reference_download_url?: string | null
}

/** Row from `character_dialog_row` / `project_cast` (Inertia + JSON). */
export type ProjectCastRow = {
  id: number
  name: string
  role: string | null
  reference_sheet_url: string | null
  reference_display_url?: string | null
  reference_download_url?: string | null
  portrait_thumb_url?: string | null
  portrait_url?: string | null
  portrait_download_url?: string | null
  active_voice_id?: number | null
  voice_ready?: boolean
  voice_provider_kind?: string | null
  outfits: ProjectCastOutfit[]
}

export async function fetchProjectCast(projectId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/project_cast`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  if (!res.ok) throw new Error(i18n.t("shot:apiErrors.loadCastFailed", { status: res.status }))
  return res.json() as Promise<ProjectCastRow[]>
}

export async function fetchProjectProps(projectId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/project_props`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  if (!res.ok) throw new Error(i18n.t("shot:apiErrors.loadPropsFailed", { status: res.status }))
  return res.json() as Promise<
    {
      id: number
      name: string
      category: string | null
      reference_sheet_url: string | null
      reference_display_url: string | null
      reference_download_url?: string | null
      front_view_thumb_url?: string | null
      front_view_url?: string | null
      front_view_download_url?: string | null
      thumb_url?: string | null
      has_front_view?: boolean
      states: {
        id: number
        name: string
        reference_url?: string | null
        reference_full_url?: string | null
        reference_download_url?: string | null
      }[]
    }[]
  >
}

export async function fetchProjectLocations(projectId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/project_locations`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  if (!res.ok) throw new Error(i18n.t("shot:apiErrors.loadLocationsFailed", { status: res.status }))
  return res.json() as Promise<
    {
      id: number
      name: string
      setting_type?: string | null
      reference_sheet_url: string | null
      reference_sheet_download_url?: string | null
      reference_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
      reference_thumb_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
      reference_version_counts_by_facing?: Partial<Record<LocationStorableFacing, number>>
      viewpoints?: LocationViewpointRow[]
      variants: {
        id: number
        name: string
        display_name: string
        reference_url?: string | null
        reference_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
        reference_thumb_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
        reference_download_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
        reference_version_counts_by_facing?: Partial<Record<LocationStorableFacing, number>>
        viewpoints?: LocationViewpointRow[]
        is_default?: boolean
      }[]
    }[]
  >
}

export type ShotSiblingSummary = {
  id: number
  number: string
  position?: number
  scene_number: number | null
  intent_summary: string | null
  storyboard_preview_url: string | null
  video_clip_url?: string | null
  video_first_frame_display_url?: string | null
}

export async function fetchShotSiblings(
  projectId: number,
  shotId: number,
): Promise<{ prev: ShotSiblingSummary | null; next: ShotSiblingSummary | null }> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/siblings`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  if (!res.ok) throw new Error(i18n.t("shot:apiErrors.loadSiblingsFailed", { status: res.status }))
  return res.json() as Promise<{ prev: ShotSiblingSummary | null; next: ShotSiblingSummary | null }>
}

export async function markShotSeen(projectId: number, shotId: number): Promise<{ changed: boolean }> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/mark_seen`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  if (!res.ok) return { changed: false }
  const data = (await res.json().catch(() => ({}))) as { changed?: boolean }
  return { changed: Boolean(data.changed) }
}

export async function markShotPreviewTabViewed(
  projectId: number,
  shotId: number,
  tab: "storyboard" | "video" | "shooting_board",
): Promise<void> {
  await fetch(`/projects/${projectId}/workspace/shots/${shotId}/preview_tabs/${tab}/mark_viewed`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
}

export type ShotPromptVersionRow = {
  id: number
  kind: "storyboard" | "video"
  system_prompt: string
  user_prompt: string
  source: "manual" | "improver" | "default"
  created_at: string
  active: boolean
  used_in_media: { id: number; kind: string; preview_url: string | null; preview_thumb_url?: string | null }[]
}

export type ShotMediaVersionPromptVersion = {
  id: number
  kind: string
  source: string
  created_at: string
  system_prompt: string
  user_prompt: string
  system_prompt_summary: string
  user_prompt_summary: string
}

export async function fetchShotPromptVersions(
  projectId: number,
  shotId: number,
  kind: "storyboard" | "video",
): Promise<ShotPromptVersionRow[]> {
  const res = await fetch(
    `/projects/${projectId}/workspace/shots/${shotId}/prompt_versions?kind=${encodeURIComponent(kind)}`,
    {
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
    },
  )
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    versions?: ShotPromptVersionRow[]
    error?: string
  }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || i18n.t("shot:apiErrors.loadPromptVersionsFailed", { status: res.status }))
  }
  return data.versions ?? []
}

export async function activateShotPromptVersion(projectId: number, shotId: number, versionId: number): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/prompt_versions/${versionId}/activate`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || i18n.t("shot:apiErrors.activatePromptVersionFailed", { status: res.status }))
  }
}

// --- Frame library (пикер кадров) ---

export type ShotFrameSourceEntityType =
  | "shot_storyboard"
  | "shot_shooting_board"
  /** Кадр версии: первый или последний, см. state_id. */
  | "shot_version_frame"
  /** Сам файл версии — видео целиком, а не кадр из него. */
  | "shot_version_media"
  | "shot_frame_capture"
  | "canvas_generation"
  /** Загрузка ноды флоу: entity_id — id ноды, файл на вложении media. */
  | "flow_node"
  | "project_upload"
  /** Эскиз из секции Библиотеки: Project#sketch_library_files. */
  | "project_sketch"
  /** Загрузка секции «Загрузки»: entity_id — id записи ProjectLibraryUpload, а не блоба. */
  | "project_library_upload"
  /** Медиа героя, локации или реквизита: листы, ракурсы и любые их версии. */
  | "project_asset"
  | "shot_reference_input"
  /** Звук шота из мультитрека: TTS, STS, запись, загрузка. Только финальный mp3. */
  | "shot_audio_asset"
  /** Сегмент аниматика: не файл, а диапазон в источнике — резолвер его не знает,
      выбор в пикере вызывающий шлёт в from_source аниматика или превиза шота. */
  | "animatic_segment"
  /** Превиз: как animatic_segment — выбор обрабатывает вызывающий. */
  | "previz"

/** Источник кадра для storyboard_from_source: резолв в blob — на сервере, строго в границах проекта. */
export type ShotFrameSource = {
  entity_type: ShotFrameSourceEntityType
  entity_id: number
  /** Только для shot_version_frame: какой кадр версии. */
  state_id?: "first" | "last"
}

export async function setShotStoryboardFromSource(
  projectId: number,
  shotId: number,
  source: ShotFrameSource,
): Promise<void> {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/storyboard_from_source`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrf,
    },
    body: JSON.stringify({ source, authenticity_token: csrf }),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
}

/** Раскадровка из кадра библиотеки: тот же путь, что storyboard_from_source; возвращает срез шота для обновления локального превью. */
export async function setShotShootingBoardFromSource(
  projectId: number,
  shotId: number,
  source: ShotFrameSource,
): Promise<{ shooting_board_preview_url?: string | null }> {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/shooting_board_from_source`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrf,
    },
    body: JSON.stringify({ source, authenticity_token: csrf }),
  })
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    error?: string
    shot?: { shooting_board_preview_url?: string | null }
  }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
  return data.shot ?? {}
}

/** Клип шота из видео библиотеки: тот же контракт source, что у storyboard_from_source. */
export async function setShotVideoFromSource(
  projectId: number,
  shotId: number,
  source: ShotFrameSource,
): Promise<void> {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/video_from_source`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrf,
    },
    body: JSON.stringify({ source, authenticity_token: csrf }),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
}

export type ShotAnimaticFromSourceResult = { ok: true; source_id: number } | { ok: true; linked: true }

/** Аниматик шота из видео библиотеки: новый шотовый ролик (source_id) или привязка готового сегмента (linked). */
export async function setShotAnimaticFromSource(
  projectId: number,
  shotId: number,
  source: ShotFrameSource,
): Promise<ShotAnimaticFromSourceResult> {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/animatic/from_source`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrf,
    },
    body: JSON.stringify({ source, authenticity_token: csrf }),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; source_id?: number }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
  return data.source_id != null ? { ok: true, source_id: data.source_id } : { ok: true, linked: true }
}

async function uploadShotMediaFile(
  projectId: number,
  shotId: number,
  path: "storyboard_upload" | "shooting_board_upload",
  file: File,
): Promise<{ shooting_board_preview_url?: string | null }> {
  const csrf = getCsrfToken()
  const form = new FormData()
  form.append("file", file)
  form.append("authenticity_token", csrf)
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrf,
    },
    body: form,
  })
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    error?: string
    shot?: { shooting_board_preview_url?: string | null }
  }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
  return data.shot ?? {}
}

/** PNG эскиза / файл с диска → новая активная версия раскадровки. */
export async function uploadShotStoryboard(projectId: number, shotId: number, file: File): Promise<void> {
  await uploadShotMediaFile(projectId, shotId, "storyboard_upload", file)
}

/** PNG эскиза / файл с диска → новая активная версия опорного кадра. */
export async function uploadShotShootingBoard(
  projectId: number,
  shotId: number,
  file: File,
): Promise<{ shooting_board_preview_url?: string | null }> {
  return uploadShotMediaFile(projectId, shotId, "shooting_board_upload", file)
}

// --- Кадры шота-перехода: ручной оверрайд вместо автоматики по соседям ---

export type TransitionFrameSide = "left" | "right"

/** Обе операции над кадрами перехода отвечают одним срезом — превью обновляется без перезагрузки шота. */
export type TransitionFramesState = {
  left_last_frame_url: string | null
  right_first_frame_url: string | null
  transition_left_frame_manual: boolean
  transition_right_frame_manual: boolean
}

async function transitionFramesRequest(
  url: string,
  method: "POST" | "DELETE",
  body: Record<string, unknown>,
): Promise<TransitionFramesState> {
  const csrf = getCsrfToken()
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrf,
    },
    body: JSON.stringify({ ...body, authenticity_token: csrf }),
  })
  const data = (await res.json().catch(() => ({}))) as Partial<TransitionFramesState> & {
    ok?: boolean
    error?: string
  }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || i18n.t("shot:apiErrors.saveFailed", { status: res.status }))
  }
  return {
    left_last_frame_url: data.left_last_frame_url ?? null,
    right_first_frame_url: data.right_first_frame_url ?? null,
    transition_left_frame_manual: data.transition_left_frame_manual === true,
    transition_right_frame_manual: data.transition_right_frame_manual === true,
  }
}

export function setShotTransitionFrameFromSource(
  projectId: number,
  shotId: number,
  side: TransitionFrameSide,
  source: ShotFrameSource,
): Promise<TransitionFramesState> {
  return transitionFramesRequest(
    `/projects/${projectId}/workspace/shots/${shotId}/transition_frame_from_source`,
    "POST",
    { side, source },
  )
}

/** Сброс к автоматике: кадр снова считается по соседнему шоту. */
export function resetShotTransitionFrame(
  projectId: number,
  shotId: number,
  side: TransitionFrameSide,
): Promise<TransitionFramesState> {
  return transitionFramesRequest(`/projects/${projectId}/workspace/shots/${shotId}/transition_frame`, "DELETE", {
    side,
  })
}

/** Срез строки media_versions, достаточный для пикера кадров и библиотеки (полный ряд — ShotMediaVersionRow в компоненте версий). */
export type ShotMediaVersionFrameRow = {
  id: number
  kind: string
  name?: string | null
  created_at: string
  active: boolean
  preview_url?: string | null
  preview_thumb_url?: string | null
  first_frame_url?: string | null
  first_frame_thumb_url?: string | null
  last_frame_url?: string | null
  last_frame_thumb_url?: string | null
  /** Поля видео-версии: нужны плееру с таймлайном (стрим same-origin, полоса кадров). */
  stream_url?: string | null
  download_url?: string | null
  duration_ms?: number | null
  frame_rate?: number | null
  timeline_frame_urls?: string[]
  timeline_frame_aspect_ratio?: number | null
}

export async function fetchShotMediaVersions(
  projectId: number,
  shotId: number,
  kind: "storyboard" | "video" | "shooting_board",
  options: { includePostProduction?: boolean; signal?: AbortSignal } = {},
): Promise<ShotMediaVersionFrameRow[]> {
  const params = new URLSearchParams({ kind })
  // include_post_production=1 — включить версии-результаты постобработки
  // (endpoint по умолчанию исключает их через excluding_post_production_results).
  if (options.includePostProduction) params.set("include_post_production", "1")
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/media_versions?${params.toString()}`, {
    credentials: "same-origin",
    signal: options.signal,
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  if (!res.ok) throw new Error(i18n.t("shot:apiErrors.loadMediaVersionsFailed", { status: res.status }))
  const data = (await res.json()) as { versions?: ShotMediaVersionFrameRow[] }
  return data.versions ?? []
}

/**
 * Загрузка аниматика прямо в шоте. XHR, а не fetch: нужен прогресс заливки —
 * видео бывает в сотни мегабайт, и без полосы это выглядит как зависание.
 */
export function uploadShotAnimatic(
  projectId: number,
  shotId: number,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ ok: boolean; error?: string; source_id?: number }> {
  return new Promise((resolve, reject) => {
    const fd = new FormData()
    fd.append("file", file)

    const xhr = new XMLHttpRequest()
    xhr.open("POST", `/projects/${projectId}/workspace/shots/${shotId}/animatic`)
    xhr.withCredentials = true
    xhr.setRequestHeader("X-CSRF-Token", getCsrfToken())
    xhr.setRequestHeader("Accept", "application/json")

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || !onProgress) return
      onProgress(Math.max(0, Math.min(99, Math.round((e.loaded / e.total) * 100))))
    }

    xhr.onload = () => {
      let data: { ok?: boolean; error?: string; source_id?: number } | null = null
      try {
        data = JSON.parse(xhr.responseText) as { ok?: boolean; error?: string; source_id?: number }
      } catch {
        data = null
      }
      if (xhr.status < 200 || xhr.status >= 300 || data?.ok === false) {
        reject(new Error(data?.error ?? i18n.t("shot:apiErrors.uploadAnimaticFailed", { status: xhr.status })))
        return
      }
      onProgress?.(100)
      resolve({ ok: true, source_id: data?.source_id })
    }

    xhr.onerror = () => reject(new Error(i18n.t("shot:apiErrors.uploadAnimaticFailed", { status: xhr.status })))
    xhr.send(fd)
  })
}

export async function unlinkAnimaticSegment(projectId: number, segmentId: number) {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/animatic_segments/${segmentId}/link_shot`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
    },
    body: JSON.stringify({ shot_id: null, authenticity_token: csrf }),
  })
  if (!res.ok) throw new Error(i18n.t("shot:apiErrors.unlinkSegmentFailed", { status: res.status }))
  return res.json() as Promise<{ ok: boolean; error?: string }>
}

// --- Animatic Segment Transcriptions ---

export type AnimaticSegmentTranscriptionResult = {
  summary?: string
  shot_type?: string
  camera?: { movement?: string; angle?: string }
  characters?: { name: string; action: string; position?: string; emotion?: string; character_id?: number }[]
  dialogue?: { speaker: string; text: string; start_sec?: number; end_sec?: number }[]
  environment?: string
  location_id?: number
  lighting?: string
  props?: string[]
  timing?: { start_sec?: number; end_sec?: number }
  animation_intent?: string
  notes_for_first_frame_prompt?: string
  notes_for_video_prompt?: string
  uncertainty_flags?: { field: string; reason: string }[]
  animatic_treatment_notes?: {
    is_reference_only?: boolean
    do_not_copy_literal_motion?: boolean
    preserve?: string[]
    ignore?: string[]
  }
}

export type MatchedCharacter = { id: number; name: string; role: string | null }
export type MatchedLocation = { id: number; name: string }

export type AnimaticSegmentTranscriptionRow = {
  id: number
  status: "pending" | "processing" | "completed" | "failed"
  model: string
  prompt_version: string
  schema_version: string
  created_at: string | null
  completed_at: string | null
  error_message: string | null
  result: AnimaticSegmentTranscriptionResult | null
  result_summary: string | null
  matched_characters: MatchedCharacter[]
  matched_location: MatchedLocation | null
}

async function patchWorkspaceEpisode(
  projectId: number,
  path: "active_episode" | "project_active_episode",
  episodeId: number,
): Promise<void> {
  await fetch(`/projects/${projectId}/workspace/${path}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ episode_id: episodeId }),
  })
}

export async function setWorkspaceActiveEpisode(projectId: number, episodeId: number): Promise<void> {
  await patchWorkspaceEpisode(projectId, "active_episode", episodeId)
}

export async function setProjectWorkingEpisode(projectId: number, episodeId: number): Promise<void> {
  await patchWorkspaceEpisode(projectId, "project_active_episode", episodeId)
}

export async function fetchAnimaticBulkStats(
  projectId: number,
  episodeId: number,
): Promise<{ ok: boolean; stats?: AnimaticBulkStats; error?: string }> {
  const res = await fetch(`/projects/${projectId}/workspace/episodes/${episodeId}/animatic/bulk_stats`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  return res.json() as Promise<{ ok: boolean; stats?: AnimaticBulkStats; error?: string }>
}

export type AnimaticBulkOperation = "extract" | "transcribe" | "create_shots"

export type AnimaticBulkPipelinePlan = {
  extract: number
  transcribe: number
  create_shots: number
}

export type AnimaticBulkBlockers = {
  video_attached: boolean
  ffmpeg_available: boolean
  active_scene: boolean
}

export type AnimaticBulkStats = {
  total: number
  with_shot: number
  without_shot: number
  without_artifacts: number
  without_transcription: number
  eligible_extract: number
  eligible_transcribe: number
  eligible_create_shots: number
  plans?: {
    extract: AnimaticBulkPipelinePlan
    transcribe: AnimaticBulkPipelinePlan
    create_shots: AnimaticBulkPipelinePlan
  }
  blockers?: AnimaticBulkBlockers
}

export async function startAnimaticBulkOperation(
  projectId: number,
  episodeId: number,
  operation: AnimaticBulkOperation,
): Promise<{ ok: boolean; job_id?: number; job?: GenerationJobRow; error?: string }> {
  const res = await fetch(`/projects/${projectId}/workspace/episodes/${episodeId}/animatic/bulk/${operation}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  return res.json() as Promise<{ ok: boolean; job_id?: number; job?: GenerationJobRow; error?: string }>
}

export async function createAnimaticSegmentTranscription(
  projectId: number,
  segmentId: number,
): Promise<{ ok: boolean; transcription?: AnimaticSegmentTranscriptionRow; error?: string }> {
  const res = await fetch(`/projects/${projectId}/workspace/animatic_segments/${segmentId}/transcriptions`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  return res.json() as Promise<{ ok: boolean; transcription?: AnimaticSegmentTranscriptionRow; error?: string }>
}

export async function fetchAnimaticSegmentTranscriptions(
  projectId: number,
  segmentId: number,
): Promise<AnimaticSegmentTranscriptionRow[]> {
  const res = await fetch(`/projects/${projectId}/workspace/animatic_segments/${segmentId}/transcriptions`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    transcriptions?: AnimaticSegmentTranscriptionRow[]
  }
  if (!res.ok || !data.ok) throw new Error(i18n.t("shot:apiErrors.loadTranscriptionsFailed", { status: res.status }))
  return data.transcriptions ?? []
}

export async function activateAnimaticSegmentTranscription(
  projectId: number,
  segmentId: number,
  transcriptionId: number,
): Promise<void> {
  const res = await fetch(
    `/projects/${projectId}/workspace/animatic_segments/${segmentId}/transcriptions/${transcriptionId}/activate`,
    {
      method: "PATCH",
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
    },
  )
  if (!res.ok) throw new Error(i18n.t("shot:apiErrors.activateTranscriptionFailed", { status: res.status }))
}

export async function retryAnimaticSegmentTranscription(
  projectId: number,
  segmentId: number,
  transcriptionId: number,
): Promise<{ ok: boolean; transcription?: AnimaticSegmentTranscriptionRow; error?: string }> {
  const res = await fetch(
    `/projects/${projectId}/workspace/animatic_segments/${segmentId}/transcriptions/${transcriptionId}/retry`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
    },
  )
  return res.json() as Promise<{ ok: boolean; transcription?: AnimaticSegmentTranscriptionRow; error?: string }>
}

export type ShotLatestTranscriptionResponse = {
  ok: boolean
  transcription?: AnimaticSegmentTranscriptionRow
  shot_character_ids?: number[]
  shot_location_variant_id?: number | null
  error?: string
}

export async function fetchShotLatestTranscription(
  projectId: number,
  shotId: number,
): Promise<ShotLatestTranscriptionResponse> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/transcription`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  return res.json() as Promise<ShotLatestTranscriptionResponse>
}

export async function applyShotTranscription(
  projectId: number,
  shotId: number,
  characterIds: number[],
  locationId: number | null,
  storyboardPrompt?: string,
  videoPrompt?: string,
): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/transcription/apply`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      character_ids: characterIds,
      location_id: locationId,
      storyboard_image_prompt: storyboardPrompt || undefined,
      video_kling_prompt: videoPrompt || undefined,
    }),
  })
  return res.json() as Promise<{ ok: boolean; error?: string }>
}

export type PostProductionParameterValue = string | number | boolean
export type PostProductionParameters = Record<string, PostProductionParameterValue>

export type PostProductionSchemaProperty = {
  type?: string | string[]
  enum?: Array<string | number | boolean>
  minimum?: number
  maximum?: number
  minLength?: number
  maxLength?: number
  pattern?: string
  description?: string
  title?: string
  default?: PostProductionParameterValue
}

export type PostProductionParametersSchema = {
  type?: string
  additionalProperties?: boolean
  required?: string[]
  properties?: Record<string, PostProductionSchemaProperty>
}

export type PostProductionModelVersionOption = {
  id: number
  name: string
  api_model_code: string
  default_version: boolean
  description: string
}

export type PostProductionModelOption = {
  id: number
  name: string
  provider: "topaz"
  description: string
  parameters_schema: PostProductionParametersSchema
  /** True when the platform provider connection is turned off. */
  platform_disabled?: boolean
  versions: PostProductionModelVersionOption[]
}

export type PostProductionVersionRow = {
  id: number
  shot_id: number
  source_shot_media_version_id: number
  result_shot_media_version_id: number | null
  status: "queued" | "processing" | "done" | "failed"
  progress: number
  parameters: PostProductionParameters
  external_task_id: string | null
  error_message: string | null
  created_at: string
  updated_at: string
  active: boolean
  model: {
    id: number
    name: string
    description: string
    parameters_schema: PostProductionParametersSchema
  }
  model_version: PostProductionModelVersionOption
  generation_job: {
    id: number
    job_kind: string
    status: string
    progress: number
    status_detail?: string | null
    error_message?: string | null
  } | null
  result: Record<string, unknown> | null
  preview_url: string | null
  download_url: string | null
  preview_thumb_url: string | null
  first_frame_url: string | null
  first_frame_thumb_url: string | null
}

export type PostProductionSourceResolution = { width: number; height: number }

export type PostProductionModelsResponse = {
  models: PostProductionModelOption[]
  source_resolution: PostProductionSourceResolution | null
}

export async function fetchPostProductionModels(
  projectId: number,
  shotId?: number | null,
): Promise<PostProductionModelsResponse> {
  const qs = shotId != null ? `?shot_id=${shotId}` : ""
  const res = await fetch(`/projects/${projectId}/workspace/post_production/models${qs}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  const data = (await res.json()) as {
    ok?: boolean
    models?: PostProductionModelOption[]
    source_resolution?: PostProductionSourceResolution
    error?: string
  }
  if (!res.ok || data.ok === false) {
    throw new Error(data.error ?? i18n.t("shot:postProduction.loadModelsFailed"))
  }
  return {
    models: Array.isArray(data.models) ? data.models : [],
    source_resolution: data.source_resolution ?? null,
  }
}

export async function createPostProductionVersion(
  projectId: number,
  shotId: number,
  payload: {
    source_version_id: number
    generation_model_id: number
    parameters: PostProductionParameters
  },
): Promise<PostProductionVersionRow> {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/post_production_versions`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(payload),
  })
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    version?: PostProductionVersionRow
    error?: string
  }
  if (!res.ok || !data.ok || !data.version) {
    throw new Error(data.error ?? i18n.t("shot:postProduction.startFailed"))
  }
  return data.version
}

export type PostProductionOriginalRow = {
  source_shot_media_version_id: number
  active: boolean
  created_at: string
  preview_url: string | null
  download_url: string | null
  preview_thumb_url: string | null
  first_frame_url: string | null
  first_frame_thumb_url: string | null
}

export async function fetchShotPostProductionVersions(
  projectId: number,
  shotId: number,
  sourceVersionId: number,
): Promise<{
  source_version: Record<string, unknown>
  original: PostProductionOriginalRow | null
  versions: PostProductionVersionRow[]
}> {
  const res = await fetch(
    `/projects/${projectId}/workspace/shots/${shotId}/post_production_versions?source_version_id=${sourceVersionId}`,
    {
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken(), "X-Requested-With": "XMLHttpRequest" },
    },
  )
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    source_version?: Record<string, unknown>
    original?: PostProductionOriginalRow
    versions?: PostProductionVersionRow[]
    error?: string
  }
  if (!res.ok || data.ok === false) {
    throw new Error(data.error ?? i18n.t("shot:postProduction.loadVersionsFailed"))
  }
  return {
    source_version: data.source_version ?? {},
    original: data.original ?? null,
    versions: Array.isArray(data.versions) ? data.versions : [],
  }
}

async function mutatePostProductionVersion(
  url: string,
  method: "PATCH" | "DELETE",
): Promise<{ ok: boolean; error?: string }> {
  const csrf = getCsrfToken()
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ authenticity_token: csrf }),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("shot:postProduction.actionFailed"))
  }
  return { ok: true }
}

export function activatePostProductionVersion(projectId: number, shotId: number, postProductionVersionId: number) {
  return mutatePostProductionVersion(
    `/projects/${projectId}/workspace/shots/${shotId}/post_production_versions/${postProductionVersionId}/activate`,
    "PATCH",
  )
}

export function resetPostProductionVersions(projectId: number, shotId: number, sourceVersionId: number) {
  return mutatePostProductionVersion(
    `/projects/${projectId}/workspace/shots/${shotId}/post_production_versions/reset?source_version_id=${sourceVersionId}`,
    "PATCH",
  )
}

export function deletePostProductionVersion(projectId: number, shotId: number, postProductionVersionId: number) {
  return mutatePostProductionVersion(
    `/projects/${projectId}/workspace/shots/${shotId}/post_production_versions/${postProductionVersionId}`,
    "DELETE",
  )
}

export type ProjectExportRow = {
  id: number
  format: string
  created_at: string
  status: string | null
  progress?: number | null
  status_detail?: string | null
  generation_job_id?: number | null
  filename?: string | null
  download_url?: string | null
  archive_expired?: boolean
}

export async function enqueueProjectExport(projectId: number, format: string): Promise<ProjectExportRow> {
  const res = await fetch(`/projects/${projectId}/workspace/export`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ format }),
  })
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    export?: ProjectExportRow
    error?: string
  }
  if (!res.ok || !data.ok || !data.export) {
    throw new Error(data.error ?? i18n.t("workspace:projectExport.enqueueFailed", { status: res.status }))
  }
  return data.export
}

export async function fetchProjectExport(projectId: number, exportId: number): Promise<ProjectExportRow> {
  const res = await fetch(`/projects/${projectId}/workspace/exports/${exportId}`, {
    method: "GET",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
  })
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    export?: ProjectExportRow
    error?: string
  }
  if (!res.ok || !data.ok || !data.export) {
    throw new Error(data.error ?? i18n.t("workspace:projectExport.fetchFailed", { status: res.status }))
  }
  return data.export
}

export async function enqueueProjectDuplicate(
  projectId: number,
  body: {
    source_project_id: number | string
    target_name: string
    options: Record<string, boolean>
    target_company_id?: number | string
  },
): Promise<number> {
  const res = await fetch(`/projects/${projectId}/workspace/duplicate`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ duplicate_project: body }),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; job_id?: number; error?: string }
  if (!res.ok || !data.ok || data.job_id == null) {
    throw new Error(data.error ?? i18n.t("workspace:settings.duplicateEnqueueFailed", { status: res.status }))
  }
  return data.job_id
}
